import { createFileRoute, Link } from '@tanstack/react-router';
import { Group, Stack, Text, Tooltip } from '@mantine/core';
import { Download, FileBarChart, Trophy } from 'lucide-react';
import { useMemo } from 'react';
import { Badge, Button, Card, EmptyState, PageContainer, PageHeader, Pill, Section, Table, type TableColumn } from '../components/ui';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { downloadCohortReportCsv, useCohortReport, useLeaderboard } from '../hooks/useGradebook';
import type { LeaderboardEntry, ReportStudent } from '../api/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = (createFileRoute as any)('/admin/gradebook/$cohortId/report')({
  component: CohortReportPage,
});

function fmtTime(s: number): string {
  if (!s) return '—';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * Institutional report — progress against ASSIGNED labs, per student, plus the
 * top-performers leaderboard (grade, then speed). Exportable as CSV.
 */
function CohortReportPage() {
  const { cohortId } = Route.useParams() as { cohortId: string };
  const report = useCohortReport(cohortId);
  const board = useLeaderboard(cohortId);
  const cohortName = (report.data?.cohort as { name?: string })?.name ?? 'cohort';

  const columns: ReadonlyArray<TableColumn<ReportStudent>> = useMemo(
    () => [
      { key: 'student', header: 'Student', cell: (r) => <Text size="sm" fw={500}>{r.username}</Text> },
      {
        key: 'progress',
        header: 'Assigned progress',
        cell: (r) => (
          <Group gap={4} wrap="nowrap">
            <Pill variant="success" size="xs">{r.completed} done</Pill>
            <Pill variant="info" size="xs">{r.in_progress} wip</Pill>
            <Pill variant={r.not_started > 0 ? 'warn' : 'neutral'} size="xs">{r.not_started} not started</Pill>
            {r.overdue > 0 ? <Pill variant="danger" size="xs">{r.overdue} overdue</Pill> : null}
          </Group>
        ),
      },
      {
        key: 'pct',
        header: 'Complete',
        align: 'right',
        cell: (r) => (
          <Text size="sm" fw={600} ff="JetBrains Mono">
            {r.completion_pct == null ? '—' : `${r.completion_pct}%`}
          </Text>
        ),
      },
      {
        key: 'grade',
        header: 'Avg grade',
        align: 'right',
        cell: (r) => (
          <Text size="sm" fw={600} ff="JetBrains Mono" c={r.avg_score_pct != null && r.avg_score_pct >= 50 ? 'rgb(var(--c-neon))' : r.avg_score_pct != null ? 'rgb(var(--c-red))' : 'rgb(var(--c-txt))'}>
            {r.avg_score_pct == null ? '—' : `${r.avg_score_pct.toFixed(0)}%`}
          </Text>
        ),
      },
      { key: 'time', header: 'Total time', align: 'right', cell: (r) => <Text size="sm" ff="JetBrains Mono">{fmtTime(r.total_time_s)}</Text> },
    ],
    [],
  );

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={FileBarChart}
        title="Cohort report"
        description={report.data ? `${cohortName} · ${report.data.assignments.length} assigned lab${report.data.assignments.length === 1 ? '' : 's'} · generated ${new Date(report.data.generated_at).toLocaleString()}` : ''}
        breadcrumbs={[
          { label: 'Forge', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Gradebook', to: '/admin/gradebook' },
          { label: 'Report' },
        ]}
        actions={
          <Button variant="secondary" leftIcon={<Download size={14} />} disabled={!report.data} onClick={() => void downloadCohortReportCsv(cohortId, cohortName)}>
            Export CSV
          </Button>
        }
      />

      {report.isError ? (
        <ErrorState title="Could not load the report" message={(report.error as Error)?.message ?? ''} onRetry={() => void report.refetch()} />
      ) : report.isPending || !report.data ? (
        <LoadingState rows={6} height={30} />
      ) : report.data.assignments.length === 0 ? (
        <EmptyState
          title="No labs assigned to this cohort"
          description="Assign labs from the cohort page to track completion, grades, and time. The report measures progress against assigned labs."
          cta={{ label: 'Go to cohorts', onClick: () => { window.location.href = '/admin/cohorts'; } }}
        />
      ) : (
        <Stack gap="lg">
          <Section title="Top performers" description="Ranked by average grade, then by time to complete — faster breaks the tie.">
            {board.isPending ? (
              <LoadingState rows={3} height={28} />
            ) : (board.data?.length ?? 0) === 0 ? (
              <Text size="sm" c="dimmed">No student has completed an assigned lab yet.</Text>
            ) : (
              <Group gap="md" wrap="wrap">
                {(board.data ?? []).slice(0, 5).map((e) => <LeaderCard key={e.user_id} e={e} />)}
              </Group>
            )}
          </Section>

          <Section title="All students" description="Completion and grade against the cohort's assigned labs.">
            <Table
              rows={report.data.students}
              rowKey={(r) => String(r.user_id)}
              columns={columns}
              emptyState={<Text size="sm" c="dimmed">No students in this cohort.</Text>}
            />
          </Section>
        </Stack>
      )}

      <Link to="/admin/gradebook" search={{ cohort: cohortId }} style={{ fontSize: 13 }}>
        ← Back to gradebook
      </Link>
    </PageContainer>
  );
}

function LeaderCard({ e }: { e: LeaderboardEntry }) {
  const medal = e.rank === 1 ? 'rgb(var(--c-amber))' : e.rank === 2 ? 'rgb(var(--c-txt-muted))' : e.rank === 3 ? 'rgb(var(--c-rose))' : 'rgb(var(--c-txt-muted))';
  return (
    <Card>
      <Group gap="sm" wrap="nowrap">
        <Tooltip label={`Rank ${e.rank}`} withArrow>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Trophy size={18} color={medal} />
            <Text fw={700} size="lg" ff="JetBrains Mono">{e.rank}</Text>
          </div>
        </Tooltip>
        <Stack gap={2}>
          <Text size="sm" fw={600}>{e.username}</Text>
          <Group gap={6}>
            <Badge variant="success" size="xs">{e.avg_score_pct.toFixed(0)}%</Badge>
            <Text size="xs" c="dimmed">{e.completed}/{e.assigned} · {fmtTime(e.total_time_s)}</Text>
          </Group>
        </Stack>
      </Group>
    </Card>
  );
}
